import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, LabelList } from 'recharts';
interface LineChartVisualProps {
  data: Array<{
    label: string;
    value: number;
  }>;
  xAxisLabel?: string;
  yAxisLabel?: string;
  lineColor?: string;
  hideValues?: boolean;
  valueDisplay?: 'tooltip' | 'on-line' | 'on-axis-line';
  mode?: 'stimulus' | 'support';
}
export function LineChartVisual({
  data,
  xAxisLabel = '',
  yAxisLabel = '',
  lineColor = '#6366f1',
  hideValues = true,
  valueDisplay,
  mode = 'stimulus'
}: LineChartVisualProps) {
  const effectiveHideValues = mode === 'support' ? false : hideValues;
  const effectiveValueDisplay = valueDisplay || (effectiveHideValues ? 'on-axis-line' : 'on-line');
  const CustomTooltip = ({
    active,
    payload
  }: {
    active?: boolean;
    payload?: Array<{
      payload: {
        label: string;
        value: number;
      };
    }>;
  }) => {
    if (!active || !payload?.[0]) return null;
    const item = payload[0].payload;
    return <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-slate-200 text-sm" data-id="element-13095" data-mp="3j94bx4wqf5ln">
        <span className="font-semibold text-slate-700" data-id="element-13096" data-mp="3f80wgjiljbky">{item.label}</span>
        {!effectiveHideValues && <span className="text-slate-500 ml-2" data-id="element-13097" data-mp="3o6cw1b1fxjpj">{item.value}</span>}
      </div>;
  };
  const CrosshairCursor = (props: any) => {
    const {
      activeCoordinate,
      width,
      height,
      left,
      top
    } = props;
    if (!activeCoordinate) return null;
    const {
      x,
      y
    } = activeCoordinate;
    return <g data-id="element-13098" data-mp="3a6lb0qcib321">
        <line x1={x} y1={top} x2={x} y2={top + height} stroke="#94a3b8" strokeDasharray="4 4" data-id="element-13099" data-mp="3a6bk90jn0rva" />
        <line x1={left} y1={y} x2={left + width} y2={y} stroke="#94a3b8" strokeDasharray="4 4" data-id="element-13100" data-mp="35ek3fhqaa0ic" />
      </g>;
  };
  return <div className="w-full max-w-3xl mx-auto flex flex-col items-center" data-id="element-13101" data-mp="2cqxnrb3s75f4">
      {/* Visually hidden table for screen readers */}
      <table className="sr-only" data-id="element-13102" data-mp="ln5wtj0gtuq5">
        <caption data-id="element-13103" data-mp="lmcoidluyx5w">
          Lijngrafiek data
          {xAxisLabel || yAxisLabel ? ` voor ${xAxisLabel} en ${yAxisLabel}` : ''}
        </caption>
        <thead data-id="element-13104" data-mp="ufg3wrg2dk36">
          <tr data-id="element-13105" data-mp="ufzcvinqky52">
            <th scope="col" data-id="element-13106" data-mp="ueeknzow9rad">{xAxisLabel || 'Categorie'}</th>
            <th scope="col" data-id="element-13107" data-mp="3tukyzqio9sxb">{yAxisLabel || 'Waarde'}</th>
          </tr>
        </thead>
        <tbody data-id="element-13108" data-mp="3twxnynmul3jv">
          {data.map((item, i) => <tr key={i} data-id="element-13109" data-mp="2pbdab1po0ti" data-mpi={i}>
              <td data-id="element-13110" data-mp="2ojslv9njni0">{item.label}</td>
              <td data-id="element-13111" data-mp="6q52d7qw3lnz">{item.value}</td>
            </tr>)}
        </tbody>
      </table>

      <div className="w-full h-[240px] md:h-[340px] bg-white rounded-xl border border-slate-200 shadow-sm p-3 md:p-4" data-id="element-13112" data-mp="boyg91a39eoo">
        <ResponsiveContainer width="100%" height="100%" data-id="element-13113" data-mp="1i5jzqcn0gfjm">
          <LineChart data={data} margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 40
        }} data-id="element-13114" data-mp="1i74ua29bryhh">
            <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-13115" data-mp="1x7a8wwr02sgj" />
            <XAxis dataKey="label" interval={0} tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={{
            stroke: '#cbd5e1'
          }} label={xAxisLabel ? {
            value: xAxisLabel,
            position: 'insideBottom',
            offset: -20,
            fill: '#475569',
            fontSize: 13,
            fontWeight: 600
          } : undefined} data-id="element-13116" data-mp="1sa0dn0gxjmks" />
            <YAxis tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={false} label={yAxisLabel ? {
            value: yAxisLabel,
            angle: -90,
            position: 'insideLeft',
            fill: '#475569',
            fontSize: 14,
            fontWeight: 600
          } : undefined} data-id="element-13117" data-mp="2yxxhu3h9rk60" />
            <Tooltip content={<CustomTooltip data-id="element-13119" data-mp="2j8sorudcwlh4" />} cursor={<CrosshairCursor data-id="element-13120" data-mp="2j7hmjyzk7ls4" />} data-id="element-13118" data-mp="2x1s681wp8prb" />

            {effectiveValueDisplay === 'on-axis-line' && Array.from(new Set(data.map(d => d.value))).map((val, i) => <ReferenceLine key={`ref-${i}`} y={val} stroke="#94a3b8" strokeDasharray="3 3" opacity={0.5} data-id="element-13121" data-mp="29ammvcrjvfc9" data-mpi={i} />)}

            <Line type="monotone" dataKey="value" stroke={lineColor} strokeWidth={3} dot={{
            fill: '#fff',
            stroke: lineColor,
            strokeWidth: 2,
            r: 5
          }} activeDot={{
            fill: '#4f46e5',
            stroke: '#312e81',
            strokeWidth: 2,
            r: 7
          }} data-id="element-13122" data-mp="20azmmuak4fks">
              {effectiveValueDisplay === 'on-line' && <LabelList dataKey="value" position="top" fill="#475569" fontSize={12} fontWeight={600} offset={10} data-id="element-13123" data-mp="1qo5dmuw64pyx" />}
            </Line>
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>;
}